import React, { useState, useEffect, useCallback } from 'react';
import { Button, Row, Col, Form, Modal, Pagination, Container, Alert } from 'react-bootstrap';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Search, 
  UserCheck, 
  UserX,
  MapPin,
  Mail,
  RotateCcw
} from 'lucide-react';
import { Clienti, ClienteItem } from '../types';
import apiService from '../services/api';
import { toast } from 'react-toastify';

const ClientiPage: React.FC = () => {
  const [clienti, setClienti] = useState<Clienti[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState<Clienti | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    indirizzo: '',
    citta: '',
    email: '',
    codice_fiscale: '',
  });

  const loadClienti = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getClienti(currentPage);
      console.log('Risposta API clienti:', response);
      
      // Estrai i clienti dalla struttura della risposta
      const clientiFromResponse = response.results?.map((item: ClienteItem) => item.cliente) || [];
      setClienti(clientiFromResponse);
      setTotalPages(response.num_pages || 1);
      setTotalCount(response.count || 0);
    } catch (error) {
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const handleSearch = useCallback(async () => {
    if (searchTerm.trim()) {
      try {
        setIsSearching(true);
        setLoading(true);
        const response = await apiService.searchCliente(searchTerm);
        console.log('Risposta ricerca:', response);
        
        // Se la risposta ha la struttura completa con paginazione
        if (response && typeof response === 'object' && 'results' in response) {
          const clientiFromResponse = response.results?.map((item: ClienteItem) => item.cliente) || [];
          setClienti(clientiFromResponse);
          setTotalPages(response.num_pages || 1);
          setTotalCount(response.count || 0);
        } else {
          // Se la risposta è un array semplice
          setClienti(response || []);
          setTotalPages(1);
          setTotalCount(response?.length || 0);
        }
      } catch (error) {
        toast.error('Errore durante la ricerca');
      } finally {
        setLoading(false);
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    loadClienti();
  }, [currentPage]); // Rimossa dipendenza loadClienti per evitare chiamate duplicate

  // Debounce per la ricerca in tempo reale
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch();
      } else if (isSearching) {
        // Se il campo di ricerca è vuoto e stavamo cercando, ricarica tutti i clienti
        setIsSearching(false);
        setCurrentPage(1);
        loadClienti();
      }
    }, 500); // 500ms di debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isSearching, loadClienti, handleSearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCliente) {
        await apiService.updateCliente(editingCliente.id, formData);
        toast.success('Cliente aggiornato con successo');
      } else {
        await apiService.createCliente(formData);
        toast.success('Cliente creato con successo');
      }
      setShowModal(false);
      resetForm();
      // Ricarica la pagina corrente
      loadClienti();
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  const handleEdit = (cliente: Clienti) => {
    setEditingCliente(cliente);
    setFormData({
      nome: cliente.nome || '',
      cognome: cliente.cognome || '',
      indirizzo: cliente.indirizzo || '',
      citta: cliente.citta || '',
      email: cliente.email || '',
      codice_fiscale: cliente.codice_fiscale || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Sei sicuro di voler eliminare questo cliente?')) {
      try {
        await apiService.deleteCliente(id);
        toast.success('Cliente eliminato con successo');
        // Ricarica la pagina corrente
        loadClienti();
      } catch (error) {
        toast.error('Errore durante l\'eliminazione');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cognome: '',
      indirizzo: '',
      citta: '',
      email: '',
      codice_fiscale: '',
    });
    setEditingCliente(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Pagina precedente
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    // Prima pagina
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    // Pagine centrali
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Ultima pagina
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    // Pagina successiva
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );

    return <Pagination>{items}</Pagination>;
  };

  // Funzione per mostrare "N/D" se il campo è null o vuoto
  const displayValue = (value: string | null | undefined) => {
    return value && value.trim() !== '' ? value : 'N/D';
  };

  if (loading) {
    return (
      <div className="page-container">
        <Container fluid>
          <div className="text-center p-5">
            <div className="modern-spinner mx-auto mb-3"></div>
            <p className="text-muted">Caricamento clienti...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Container fluid>
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>Gestione Clienti</h1>
              <p className="text-muted">Visualizza e gestisci l'anagrafica clienti</p>
            </div>
            <Button 
              variant="primary" 
              type="button" 
              onClick={() => setShowModal(true)}
              className="btn-modern d-flex align-items-center gap-2"
            >
              <Plus size={20} />
              Nuovo Cliente
            </Button>
          </div>
        </div>

        <section className="inv-stats">
          <div className="inv-stat">
            <span>Clienti totali</span>
            <strong>{totalCount}</strong>
          </div>
          <div className="inv-stat">
            <span>Attivi in questa pagina</span>
            <strong>{clienti.filter((cliente) => cliente.is_active).length}</strong>
          </div>
          <div className="inv-stat">
            <span>Inattivi in questa pagina</span>
            <strong>{clienti.filter((cliente) => !cliente.is_active).length}</strong>
          </div>
        </section>

        {/* Barra di ricerca */}
        <div className="inv-search">
          <div className="inv-search-field">
            <Search size={18} />
            <Form.Control
              type="text"
              placeholder="Cerca per nome, cognome, email o codice fiscale..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {isSearching && (
            <span className="inv-chip">
              <Search size={14} />
              Ricerca attiva
            </span>
          )}
          <Button
            variant="outline-secondary"
            type="button"
            onClick={loadClienti}
            title="Azzera la ricerca"
          >
            <RotateCcw size={16} />
            Reset ricerca
          </Button>
        </div>

        <div className="inv-list is-cli">
          <div className="inv-head">
            <span>Cliente</span>
            <span>Contatti</span>
            <span>Codice fiscale</span>
            <span>Stato</span>
            <span />
          </div>

          {clienti.length === 0 && (
            <div className="set-empty m-3">
              <Users size={34} />
              <strong>Nessun cliente trovato</strong>
              <span>Prova a cambiare la ricerca o aggiungi un nuovo cliente.</span>
            </div>
          )}

          {clienti.map((cliente, index) => (
            <article key={cliente.id} className="inv-row" style={{ animationDelay: `${index * 0.04}s` }}>
              <div className="inv-client">
                <span className="inv-avatar">
                  {`${cliente.nome?.[0] || ''}${cliente.cognome?.[0] || ''}`.toUpperCase() || '?'}
                </span>
                <div>
                  <strong>{displayValue(cliente.nome)} {displayValue(cliente.cognome)}</strong>
                  <small>{displayValue(cliente.citta)}</small>
                </div>
              </div>

              <div className="inv-contacts">
                <span><Mail size={14} /> {displayValue(cliente.email)}</span>
                <span><MapPin size={14} /> {displayValue(cliente.indirizzo)}</span>
              </div>

              <div>
                <code className="inv-code">{displayValue(cliente.codice_fiscale)}</code>
              </div>

              <div>
                <span className={`inv-pay ${cliente.is_active ? 'is-pos' : 'is-off'}`}>
                  {cliente.is_active ? <UserCheck size={14} /> : <UserX size={14} />}
                  {cliente.is_active ? 'Attivo' : 'Inattivo'}
                </span>
              </div>

              <div className="inv-actions">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  type="button"
                  onClick={() => handleEdit(cliente)}
                  className="btn-sm-modern"
                  title="Modifica cliente"
                >
                  <Edit size={14} />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  type="button"
                  onClick={() => handleDelete(cliente.id)}
                  className="btn-sm-modern"
                  title="Elimina cliente"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </article>
          ))}

          {/* Paginazione */}
          {totalPages > 1 && (
            <div className="inv-foot justify-content-center">
              <Pagination className="modern-pagination mb-0">
                {renderPagination()}
              </Pagination>
            </div>
          )}
        </div>

        {/* Modal per creazione/modifica */}
        <Modal show={showModal} onHide={() => setShowModal(false)} className="modern-modal">
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2">
              <Users size={20} />
              {editingCliente ? 'Modifica Cliente' : 'Nuovo Cliente'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit} className="modern-form">
            <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nome *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cognome *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.cognome}
                    onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Indirizzo</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.indirizzo}
                    onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Città</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.citta}
                    onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Codice Fiscale</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.codice_fiscale}
                    onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="secondary" 
                type="button" 
                onClick={() => setShowModal(false)}
                className="btn-modern"
              >
                Annulla
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                className="btn-modern d-flex align-items-center gap-2"
              >
                <Users size={16} />
                {editingCliente ? 'Aggiorna' : 'Crea'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default ClientiPage; 